import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import * as paths from 'src/constants/paths'
import { Intent } from '@blueprintjs/core/lib/cjs/common/intent'
import { Toaster } from '@blueprintjs/core/lib/cjs/components/toast/toaster'
import { Spinner } from '@blueprintjs/core/lib/cjs/components/spinner/spinner'
import CreateTemplateModal from 'src/components/Template/CreateTemplateModal'
import DeleteTemplateModal from 'src/components/Template/DeleteTemplateModal'
import DataPlaceholder from 'src/components/DataPlaceholder'
import List from 'src/components/List'
import Pagination from 'src/utils/pagination'
import { getNextSortState } from 'src/utils/queryUtils'
import noRunbooksImage from 'src/assets/icons/no-runbooks.svg'
import { CHANGE_RUNBOOK_TEMPLATES_PAGE } from 'src/constants/actionTypes'
import {
    getRunbookTemplatesFromServer,
    updateRunbookTemplate,
    deleteRunbookTemplateFromServer,
    swapRunbookTemplatePriority,
} from 'src/store/modules/runbooks'
import RunbooksTemplatesFilters from './RunbooksTemplatesFilters'
import getTableConfig, { filterConfig as baseFilterConfig } from './tableConfig'
import {
    ITEMS_PER_PAGE,
    FILTER_ANY_VALUE,
    FILTER_ANY_LABEL,
    INITIAL_FILTERS,
    ORGANIZATION_SCOPE_ALL_VALUE,
    buildOrganizationFilterOptions,
    buildRunbookTemplatesApiFilters,
    buildRunbookTemplatesSortQuery,
    handleSwapPriorityDown,
    handleSwapPriorityUp,
    shouldSkipRunbookTemplatesRequest,
} from './utils'
import styles from './runbooksTemplates.module.scss'

/**
 * RunbooksTemplates
 * Placeholder component for the Templates tab on the Runbooks overview page.
 * This component will be expanded in a future feature ticket to include:
 * - A searchable / filterable table of runbook templates
 * - Row-level action menus (create from template, edit, delete)
 * - Template creation workflow
 * @component
 * @returns {JSX.Element}
 */
const RunbooksTemplates = (props) => {
    const {
        templates,
        isLoading,
        page,
        requestedPage,
        requestedSort,
        requestedRequestKey,
        loadedPage,
        loadedSort,
        loadedRequestKey,
        totalElements,
        totalPages,
        perPage,
        getRunbookTemplatesFromServer,
        navigateToTemplateDetail,
        updateRunbookTemplate,
        deleteRunbookTemplateFromServer,
        changeRunbookTemplatesPage,
        swapRunbookTemplatePriority,
    } = props
    const [searchQuery, setSearchQuery] = useState('')
    const [filters, setFilters] = useState(INITIAL_FILTERS)
    const [sortBy, setSortBy] = useState(null)
    const [sortDirection, setSortDirection] = useState(1)
    const [organizationFilterOptions, setOrganizationFilterOptions] = useState([
        { value: FILTER_ANY_VALUE, label: FILTER_ANY_LABEL },
    ])

    useEffect(() => {
        if (!templates.length) {
            return
        }

        const nextPageOptions = buildOrganizationFilterOptions(
            templates,
            FILTER_ANY_VALUE
        )

        setOrganizationFilterOptions((previousOptions) => {
            const mergedByValue = new Map()

            for (const option of previousOptions) {
                if (option.value !== FILTER_ANY_VALUE) {
                    mergedByValue.set(String(option.value), option)
                }
            }

            for (const option of nextPageOptions) {
                if (option.value !== FILTER_ANY_VALUE) {
                    mergedByValue.set(String(option.value), option)
                }
            }

            const mergedEntries = Array.from(mergedByValue.values())
            const allOrganizationEntry = mergedEntries.find(
                (entry) =>
                    String(entry.value).toLowerCase() ===
                    ORGANIZATION_SCOPE_ALL_VALUE
            )
            const sortedOrganizationEntries = mergedEntries
                .filter(
                    (entry) =>
                        String(entry.value).toLowerCase() !==
                        ORGANIZATION_SCOPE_ALL_VALUE
                )
                .sort((a, b) =>
                    String(a.label).localeCompare(String(b.label), undefined, {
                        sensitivity: 'base',
                        numeric: true,
                    })
                )

            const mergedOptions = [
                { value: FILTER_ANY_VALUE, label: FILTER_ANY_LABEL },
                ...(allOrganizationEntry ? [allOrganizationEntry] : []),
                ...sortedOrganizationEntries,
            ]

            const isUnchanged =
                previousOptions.length === mergedOptions.length &&
                previousOptions.every(
                    (option, index) =>
                        option.value === mergedOptions[index].value &&
                        option.label === mergedOptions[index].label
                )

            return isUnchanged ? previousOptions : mergedOptions
        })
    }, [templates])

    const computedFilterConfig = useMemo(
        () => ({
            ...baseFilterConfig,
            organization: organizationFilterOptions,
        }),
        [organizationFilterOptions]
    )
    const currentSort = buildRunbookTemplatesSortQuery(sortBy, sortDirection)
    const normalizedSearchQuery = searchQuery.trim()
    const apiFilters = useMemo(() => buildRunbookTemplatesApiFilters(filters), [
        filters,
    ])
    const currentRequestKey = useMemo(
        () =>
            JSON.stringify({
                page,
                sort: currentSort,
                search: normalizedSearchQuery,
                ...apiFilters,
            }),
        [apiFilters, page, currentSort, normalizedSearchQuery]
    )
    const [visibleTemplates, setVisibleTemplates] = useState(templates)
    const [isSwappingPriority, setIsSwappingPriority] = useState(false)
    const [editDialog, setEditDialog] = useState({
        open: false,
        template: null,
    })
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        template: null,
    })
    const [isDeleting, setIsDeleting] = useState(false)
    const toasterRef = useRef(null)

    useEffect(() => {
        setVisibleTemplates(templates)
    }, [templates])

    useEffect(() => {
        if (
            shouldSkipRunbookTemplatesRequest({
                isLoading,
                loadedRequestKey,
                currentRequestKey,
                requestedRequestKey,
                loadedPage,
                currentPage: page,
                loadedSort,
                currentSort,
                requestedPage,
                requestedSort,
            })
        ) {
            return
        }

        getRunbookTemplatesFromServer({
            page,
            per_page: ITEMS_PER_PAGE,
            sort: currentSort,
            search: normalizedSearchQuery,
            ...apiFilters,
        })
    }, [
        apiFilters,
        page,
        currentRequestKey,
        currentSort,
        getRunbookTemplatesFromServer,
        isLoading,
        loadedPage,
        loadedRequestKey,
        loadedSort,
        normalizedSearchQuery,
        requestedPage,
        requestedRequestKey,
        requestedSort,
    ])

    useEffect(() => {
        if (page > totalPages) {
            changeRunbookTemplatesPage(totalPages)
        }
    }, [changeRunbookTemplatesPage, page, totalPages])

    const resetToFirstPageIfNeeded = () => {
        if (page !== 1) {
            changeRunbookTemplatesPage(1)
        }
    }

    const handleSortColumn = (column) => {
        const { nextSortBy, nextSortDirection } = getNextSortState({
            currentSortBy: sortBy,
            currentSortDirection: sortDirection,
            column,
        })

        setSortBy(nextSortBy)
        setSortDirection(nextSortDirection)
        resetToFirstPageIfNeeded()
    }

    const handleFilterChange = (key, value) => {
        setFilters((previousFilters) => ({
            ...previousFilters,
            [key]: value,
        }))
        resetToFirstPageIfNeeded()
    }

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value)
        resetToFirstPageIfNeeded()
    }

    const handlePageChange = (newPage) => {
        changeRunbookTemplatesPage(newPage)
    }

    const showToast = useCallback((message, intent) => {
        if (toasterRef.current) {
            toasterRef.current.show({ message, intent, timeout: 3000 })
        }
    }, [])

    const isAnyFilterActive =
        searchQuery.trim().length > 0 ||
        Object.values(filters).some((value) => value !== FILTER_ANY_VALUE)
    const isSortActive = Boolean(sortBy)
    const isPrioritySwapDisabled = isAnyFilterActive || isSortActive
    const shouldShowPagination = totalElements > perPage

    const swapPriorityContext = useMemo(
        () => ({
            page,
            totalPages,
            visibleTemplates,
            isPrioritySwapDisabled,
            isSwappingPriority,
            currentSort,
            normalizedSearchQuery,
            apiFilters,
            swapRunbookTemplatePriority,
            getRunbookTemplatesFromServer,
            changeRunbookTemplatesPage,
            setVisibleTemplates,
            setIsSwappingPriority,
            showToast,
            dangerIntent: Intent.DANGER,
        }),
        [
            page,
            totalPages,
            visibleTemplates,
            isPrioritySwapDisabled,
            isSwappingPriority,
            currentSort,
            normalizedSearchQuery,
            apiFilters,
            swapRunbookTemplatePriority,
            getRunbookTemplatesFromServer,
            changeRunbookTemplatesPage,
            setVisibleTemplates,
            setIsSwappingPriority,
            showToast,
        ]
    )

    const handleTemplateSwapUp = (template) => {
        handleSwapPriorityUp(template, swapPriorityContext)
    }

    const handleTemplateSwapDown = (template) => {
        handleSwapPriorityDown(template, swapPriorityContext)
    }

    const renderNoTemplates = () => {
        if (!visibleTemplates.length && !isLoading && isAnyFilterActive) {
            return (
                <DataPlaceholder
                    title="No results found."
                    subtitle=""
                    img={noRunbooksImage}
                />
            )
        }

        if (!visibleTemplates.length && !isLoading) {
            return (
                <DataPlaceholder
                    title="No Template have been created yet."
                    subtitle={
                        <>
                            Click at the right top corner on{' '}
                            <b>"Create runbook Template"</b> button to start
                            creating your first template.
                        </>
                    }
                    img={noRunbooksImage}
                />
            )
        }

        return (
            isLoading && (
                <div className="full-width" style={{ marginTop: '10%' }}>
                    <Spinner size={Spinner.SIZE_STANDARD} />
                </div>
            )
        )
    }

    const handleEditTemplate = (runbook) => {
        setEditDialog({ open: true, template: runbook })
    }

    const handleDeleteTemplate = (runbook) => {
        setConfirmDialog({ open: true, template: runbook })
    }

    const handleConfirmDelete = async () => {
        if (!confirmDialog.template) {
            return
        }

        const templateToDelete = confirmDialog.template
        const templateId = templateToDelete.id

        if (!templateId) {
            showToast(
                'Unable to delete template: missing template ID.',
                Intent.DANGER
            )
            return
        }

        setIsDeleting(true)
        try {
            await deleteRunbookTemplateFromServer(templateId)

            await getRunbookTemplatesFromServer({
                page,
                per_page: ITEMS_PER_PAGE,
                sort: currentSort,
                search: normalizedSearchQuery,
                ...apiFilters,
            })

            setConfirmDialog({ open: false, template: null })
            showToast(
                `Template "${templateToDelete.name}" deleted successfully.`,
                Intent.SUCCESS
            )
        } catch (err) {
            showToast(
                'Failed to delete template. Please try again.',
                Intent.DANGER
            )
        } finally {
            setIsDeleting(false)
        }
    }

    const handleCancelEdit = () => {
        setEditDialog({ open: false, template: null })
    }

    const handleSaveEdit = async (payload) => {
        const templateId = editDialog.template?.id

        if (!templateId) {
            showToast(
                'Unable to edit template: missing template ID.',
                Intent.DANGER
            )
            return
        }

        try {
            await updateRunbookTemplate(templateId, payload)
            await getRunbookTemplatesFromServer({
                page,
                per_page: ITEMS_PER_PAGE,
                sort: currentSort,
                search: normalizedSearchQuery,
                ...apiFilters,
            })

            setEditDialog({ open: false, template: null })
        } catch (_error) {
            showToast(
                'Failed to update template. Please try again.',
                Intent.DANGER
            )
        }
    }

    const handleTemplateLinkClick = (event, runbook) => {
        event.preventDefault()
        if (runbook?.id) {
            navigateToTemplateDetail(runbook.id)
        }
    }

    const renderPage = () => (
        <List
            onSort={handleSortColumn}
            sortBy={sortBy}
            sortDirection={sortDirection}
            headerClassName={styles.listHeader}
            rowClassName={styles.listRow}
            config={getTableConfig({
                actions: {
                    onDeleteTemplate: handleDeleteTemplate,
                    onEditTemplate: handleEditTemplate,
                    onTemplateLinkClick: handleTemplateLinkClick,
                    onSwapTemplateUp: handleTemplateSwapUp,
                    onSwapTemplateDown: handleTemplateSwapDown,
                },
                swapState: {
                    isPrioritySwapDisabled,
                    isSwappingPriority,
                },
                pagination: {
                    currentPage: page,
                    totalPages,
                },
            })}
            data={visibleTemplates}
        />
    )

    return (
        <div className={styles.wrapper}>
            <Toaster ref={toasterRef} position="top-right" />
            <RunbooksTemplatesFilters
                filterOptions={computedFilterConfig}
                filters={filters}
                searchQuery={searchQuery}
                anyValue={FILTER_ANY_VALUE}
                onFilterChange={handleFilterChange}
                onSearchChange={handleSearchChange}
            />

            <div className={styles.listWrapper}>
                {renderNoTemplates() ||
                    (shouldShowPagination ? (
                        <Pagination
                            currentPage={page}
                            totalElements={totalElements}
                            totalPages={totalPages}
                            perPage={perPage}
                            onPageChange={handlePageChange}
                            renderPage={renderPage}
                        />
                    ) : (
                        renderPage()
                    ))}
            </div>

            {editDialog.open ? (
                <CreateTemplateModal
                    onCancel={handleCancelEdit}
                    onSave={handleSaveEdit}
                    initialValues={editDialog.template}
                    title="Edit runbook template"
                    submitLabel="Save"
                />
            ) : null}

            <DeleteTemplateModal
                isOpen={confirmDialog.open}
                template={confirmDialog.template}
                isDeleting={isDeleting}
                onCancel={() =>
                    setConfirmDialog({ open: false, template: null })
                }
                onConfirm={handleConfirmDelete}
            />
        </div>
    )
}

const mapStateToProps = (state) => {
    const { templates } = state.runbooks

    return {
        templates: templates.values.map(
            (templateId) => templates.byId[templateId]
        ),
        isLoading: templates.loading,
        page: templates.page,
        requestedPage: templates.requestedPage,
        requestedSort: templates.requestedSort || '',
        requestedRequestKey: templates.requestedRequestKey || '',
        loadedPage: templates.loadedPage,
        loadedSort: templates.loadedSort || '',
        loadedRequestKey: templates.loadedRequestKey || '',
        totalElements: templates.totalElements || 0,
        totalPages: templates.totalPages || 1,
        perPage: templates.perPage || ITEMS_PER_PAGE,
    }
}

const mapDispatchToProps = (dispatch) => ({
    navigateToTemplateDetail: (templateId) =>
        dispatch(push(paths.TEMPLATE_DETAIL_PATH.replace(':templateId', String(templateId)))),
    changeRunbookTemplatesPage: (newPage) =>
        dispatch({
            type: CHANGE_RUNBOOK_TEMPLATES_PAGE,
            page: newPage,
        }),
    getRunbookTemplatesFromServer: bindActionCreators(
        getRunbookTemplatesFromServer,
        dispatch
    ),
    updateRunbookTemplate: bindActionCreators(updateRunbookTemplate, dispatch),
    deleteRunbookTemplateFromServer: bindActionCreators(
        deleteRunbookTemplateFromServer,
        dispatch
    ),
    swapRunbookTemplatePriority: bindActionCreators(
        swapRunbookTemplatePriority,
        dispatch
    ),
})

export { RunbooksTemplates }

export default connect(mapStateToProps, mapDispatchToProps)(RunbooksTemplates)
